import router from '../router'

/** Base URL for API requests (empty = same origin). Set in entrypoint if needed. */
export let apiBaseUrl = ''

/**
 * Centralized API utility that handles authentication and 401 responses
 * Automatically redirects to login page on Unauthorized (401) responses.
 * Catches network errors (e.g. Failed to fetch) and rethrows with a clear message.
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = localStorage.getItem('auth_token')

  const headers = new Headers(options.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  // Send app locale so API returns validation messages in the same language
  const locale = localStorage.getItem('locale') || 'en'
  headers.set('Accept-Language', locale === 'ru' ? 'ru' : 'en')

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const fullUrl = apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}` : url

  let response: Response
  try {
    response = await fetch(fullUrl, {
      ...options,
      headers,
    })
  } catch (err) {
    const message = err instanceof TypeError && err.message === 'Failed to fetch'
      ? 'Network error: unable to reach the server. Check your connection and that the server is running.'
      : err instanceof Error
        ? err.message
        : 'Network request failed'
    throw new Error(message, { cause: err })
  }

  if (response.status === 401) {
    localStorage.removeItem('auth_token')
    router.push('/login')
    throw new Error('Unauthorized - redirecting to login')
  }

  return response
}

/**
 * Convenience method for GET requests
 */
export async function apiGet(url: string): Promise<Response> {
  return apiFetch(url, { method: 'GET' })
}

/**
 * Convenience method for POST requests
 */
export async function apiPost(url: string, body?: any): Promise<Response> {
  return apiFetch(url, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * Convenience method for PUT requests
 */
export async function apiPut(url: string, body?: any): Promise<Response> {
  return apiFetch(url, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * Convenience method for DELETE requests
 */
export async function apiDelete(url: string): Promise<Response> {
  return apiFetch(url, { method: 'DELETE' })
}

/**
 * Parse error message from API response body.
 * Supports: { errors: string[] }, { error: string }, { message: string }.
 * Use when !response.ok to show validation/DB errors to the user.
 */
export async function getErrorMessage(response: Response, fallback = 'Unknown error'): Promise<string> {
  const contentType = response.headers.get('Content-Type') || ''
  if (!contentType.includes('application/json')) {
    return response.statusText || fallback
  }
  try {
    // Response body can only be read once
    const data = await response.json()
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const text = data.errors.join(', ')
      return text || fallback
    }
    if (typeof data.error === 'string' && data.error) {
      return data.error
    }
    if (typeof data.message === 'string' && data.message) {
      return data.message
    }
  } catch {
    // Body may already be consumed or invalid JSON
  }
  return fallback
}

